import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useT } from '../i18n';

interface Subscription {
  platformOwner?: boolean;
  name: string;
  status: string;
  plan: string;
  planName: string;
  trialEndsAt: string | null;
  paidUntil: string | null;
  expiresAt: string | null;
  daysLeft: number | null;
  messagesSent: number;
  messagesLimit: number;
  dailyLimit?: number;
  messagesSentToday?: number;
  unlimitedMessages?: boolean;
  ownerPhone: string | null;
  createdAt: string;
  canSend: boolean;
  blockMessage: string | null;
  quotas: {
    maxUsers: number;
    maxContacts: number;
    maxCampaigns: number;
    maxConnections: number;
    maxMessagesPerMonth: number;
  } | null;
  counts: { users: number; contacts: number; campaigns: number; connections: number };
}

interface TeamMember {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  role: string;
  ativo: boolean;
  ultimoLogin: string | null;
}

// O nome do plano vem do servidor em inglês; traduzimos na hora de mostrar
const PLAN_KEYS: Record<string, string> = {
  'Free trial': 'sub.planTrial',
  Starter: 'sub.planStarter',
  Pro: 'sub.planPro',
  Business: 'sub.planBusiness',
  'Internal (no limits)': 'sub.planInternal',
};

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString() : '—';
}

/** Barra de consumo: fica âmbar a partir de 80% e vermelha ao atingir o limite */
function UsageBar({ label, used, limit, unlimitedText }: { label: string; used: number; limit: number; unlimitedText: string }) {
  const unlimited = !limit || limit <= 0;
  const percentage = unlimited ? 0 : Math.min(Math.round((used / limit) * 100), 100);
  const color = unlimited || percentage < 80 ? 'bg-emerald-500' : percentage < 100 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-semibold text-gray-900">
          {unlimited ? `${formatNumber(used)} · ${unlimitedText}` : `${formatNumber(used)} / ${formatNumber(limit)}`}
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full mt-3 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${unlimited ? 3 : Math.max(percentage, used > 0 ? 3 : 0)}%` }} />
      </div>
    </div>
  );
}

export function CompanyPage() {
  const t = useT();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  const load = useCallback(async () => {
    try {
      const [subscriptionResponse, usersResponse] = await Promise.all([
        fetch('/api/subscription', { headers: authHeaders() }),
        fetch('/api/users?pageSize=100', { headers: authHeaders() }),
      ]);

      if (subscriptionResponse.ok) {
        setSubscription(await subscriptionResponse.json());
      } else {
        throw new Error(String(subscriptionResponse.status));
      }

      if (usersResponse.ok) {
        const data = await usersResponse.json();
        setTeam(data?.data?.users || data?.users || []);
      }
    } catch {
      toast.error(t('company.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const statusLabel = (status: string) =>
    status === 'ACTIVE' ? t('company.statusActive')
      : status === 'SUSPENDED' ? t('company.statusSuspended')
      : t('company.statusTrial');

  const roleLabel = (role: string) =>
    role === 'SUPERADMIN' ? t('company.roleSuper')
      : role === 'ADMIN' ? t('company.roleAdmin')
      : t('company.roleUser');

  return (
    <>
      <Header title={t('company.title')} subtitle={t('company.subtitle')} />

      <div className="p-6 space-y-6">
        {loading && <div className="text-center text-gray-400 py-12">{t('common.loading')}</div>}

        {!loading && subscription?.platformOwner && (
          <div className="rounded-xl border border-gray-200 p-8 text-center text-gray-500">
            {t('company.platformOwner')}
          </div>
        )}

        {!loading && subscription && !subscription.platformOwner && (
          <>
            {/* Cabeçalho da empresa */}
            <div className="rounded-xl border border-gray-200 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900" dir="auto">{subscription.name}</h2>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      subscription.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-800'
                        : subscription.status === 'SUSPENDED' ? 'bg-red-100 text-red-800'
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {statusLabel(subscription.status)}
                    </span>
                    <span>{t('company.plan')}: <strong className="text-gray-900">
                      {PLAN_KEYS[subscription.planName] ? t(PLAN_KEYS[subscription.planName]) : subscription.planName}
                    </strong></span>
                    {subscription.ownerPhone && (
                      <span dir="ltr">{t('company.ownerPhone')}: {subscription.ownerPhone}</span>
                    )}
                    <span>{t('company.since')}: {formatDate(subscription.createdAt)}</span>
                  </div>
                </div>

                <div className="text-end">
                  <div className="text-xs text-gray-500">{t('company.renews')}</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {subscription.expiresAt ? formatDate(subscription.expiresAt) : t('company.noExpiry')}
                  </div>
                  {subscription.daysLeft !== null && (
                    <div className={`text-xs mt-1 ${subscription.daysLeft <= 3 ? 'text-red-600' : 'text-gray-500'}`}>
                      {subscription.daysLeft >= 0
                        ? t('company.daysLeft', { days: subscription.daysLeft })
                        : t('company.expired')}
                    </div>
                  )}
                </div>
              </div>

              {!subscription.canSend && subscription.blockMessage && (
                <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                  {t('company.blocked', { reason: subscription.blockMessage })} {t('company.upgradeHint')}
                </div>
              )}
            </div>

            {/* Consumo */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">{t('company.usage')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                <UsageBar
                  label={t('company.messagesMonth')}
                  used={subscription.messagesSent}
                  limit={subscription.messagesLimit}
                  unlimitedText={t('company.unlimited')}
                />
                {(subscription.dailyLimit || 0) > 0 && (
                  <UsageBar
                    label={t('company.messagesDay')}
                    used={subscription.messagesSentToday || 0}
                    limit={subscription.dailyLimit || 0}
                    unlimitedText={t('company.unlimited')}
                  />
                )}
                <UsageBar label={t('company.contacts')} used={subscription.counts.contacts} limit={subscription.quotas?.maxContacts || 0} unlimitedText={t('company.unlimited')} />
                <UsageBar label={t('company.campaigns')} used={subscription.counts.campaigns} limit={subscription.quotas?.maxCampaigns || 0} unlimitedText={t('company.unlimited')} />
                <UsageBar label={t('company.connections')} used={subscription.counts.connections} limit={subscription.quotas?.maxConnections || 0} unlimitedText={t('company.unlimited')} />
                <UsageBar label={t('company.users')} used={subscription.counts.users} limit={subscription.quotas?.maxUsers || 0} unlimitedText={t('company.unlimited')} />
              </div>
            </div>

            {/* Equipe */}
            <div className="rounded-xl border border-gray-200">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold text-gray-900">
                  {t('company.team')} <span className="text-gray-400 font-normal">({team.length})</span>
                </h3>
                {isAdmin && (
                  <Link to="/users" className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700">
                    {t('company.manageUsers')}
                  </Link>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-start text-gray-500">
                    <tr>
                      <th className="text-start px-4 py-2">{t('users.colName')}</th>
                      <th className="text-start px-4 py-2">{t('users.colEmail')}</th>
                      <th className="text-start px-4 py-2">{t('users.colRole')}</th>
                      <th className="text-start px-4 py-2">{t('users.colStatus')}</th>
                      <th className="text-start px-4 py-2">{t('company.lastLogin')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {team.map(member => (
                      <tr key={member.id} className="border-t">
                        <td className="px-4 py-2 font-medium text-gray-900" dir="auto">{member.nome}</td>
                        <td className="px-4 py-2 text-gray-600" dir="ltr">{member.telefone || member.email}</td>
                        <td className="px-4 py-2">{roleLabel(member.role)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${member.ativo ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'}`}>
                            {member.ativo ? t('company.activeUser') : t('company.inactiveUser')}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {member.ultimoLogin ? formatDate(member.ultimoLogin) : t('company.never')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default CompanyPage;
